import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  Calendar,
  Trophy,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  Edit3,
  Star,
  Award,
  BookOpen,
  Users,
  Briefcase,
  Heart,
  Lightbulb,
  BarChart3,
  Filter
} from 'lucide-react';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';
import { ProgressBar } from '../components/shared/ProgressBar';

interface Goal {
  id: string;
  title: string;
  description: string;
  category: 'academic' | 'extracurricular' | 'personal' | 'career' | 'college-prep';
  priority: 'high' | 'medium' | 'low';
  status: 'not-started' | 'in-progress' | 'completed' | 'paused';
  progress: number;
  targetDate: string;
  createdDate: string;
  milestones: {
    id: string;
    title: string;
    completed: boolean;
    dueDate: string;
  }[];
  tags: string[];
}

const goals: Goal[] = [
  {
    id: '1',
    title: 'Achieve 1500+ SAT Score',
    description: 'Prepare for and achieve a competitive SAT score of 1500 or higher to strengthen college applications.',
    category: 'academic',
    priority: 'high',
    status: 'in-progress',
    progress: 75,
    targetDate: '2025-03-15',
    createdDate: '2024-09-01',
    milestones: [
      { id: '1a', title: 'Complete diagnostic test', completed: true, dueDate: '2024-09-15' },
      { id: '1b', title: 'Finish prep course', completed: true, dueDate: '2024-11-30' },
      { id: '1c', title: 'Take practice tests weekly', completed: false, dueDate: '2025-02-28' },
      { id: '1d', title: 'Register for official test', completed: false, dueDate: '2025-01-15' }
    ],
    tags: ['standardized-testing', 'college-prep', 'academics']
  },
  {
    id: '2',
    title: 'Lead Environmental Club to State Recognition',
    description: 'Expand our environmental club\'s impact and achieve state-level recognition for our sustainability initiatives.',
    category: 'extracurricular',
    priority: 'high',
    status: 'in-progress',
    progress: 60,
    targetDate: '2025-05-01',
    createdDate: '2024-08-15',
    milestones: [
      { id: '2a', title: 'Recruit 20+ new members', completed: true, dueDate: '2024-10-01' },
      { id: '2b', title: 'Launch campus recycling program', completed: true, dueDate: '2024-11-15' },
      { id: '2c', title: 'Organize Earth Day event', completed: false, dueDate: '2025-04-22' },
      { id: '2d', title: 'Submit state award application', completed: false, dueDate: '2025-03-01' }
    ],
    tags: ['leadership', 'environment', 'community-impact']
  },
  {
    id: '3',
    title: 'Complete 100 Hours of Community Service',
    description: 'Volunteer 100 hours at local organizations to give back to the community and strengthen college applications.',
    category: 'personal',
    priority: 'medium',
    status: 'in-progress',
    progress: 65,
    targetDate: '2025-06-01',
    createdDate: '2024-07-01',
    milestones: [
      { id: '3a', title: 'Complete 25 hours at animal shelter', completed: true, dueDate: '2024-09-30' },
      { id: '3b', title: 'Volunteer 25 hours at food bank', completed: true, dueDate: '2024-11-30' },
      { id: '3c', title: 'Tutor students for 25 hours', completed: false, dueDate: '2025-02-28' },
      { id: '3d', title: 'Environmental cleanup - 25 hours', completed: false, dueDate: '2025-05-31' }
    ],
    tags: ['community-service', 'volunteering', 'personal-growth']
  },
  {
    id: '4',
    title: 'Research Summer Internship Opportunities',
    description: 'Find and apply to competitive summer internships in STEM fields to gain hands-on experience.',
    category: 'career',
    priority: 'high',
    status: 'not-started',
    progress: 15,
    targetDate: '2025-02-01',
    createdDate: '2024-10-01',
    milestones: [
      { id: '4a', title: 'Research 20+ programs', completed: false, dueDate: '2024-11-15' },
      { id: '4b', title: 'Prepare application materials', completed: false, dueDate: '2024-12-15' },
      { id: '4c', title: 'Submit 5 applications', completed: false, dueDate: '2025-01-15' },
      { id: '4d', title: 'Complete interviews', completed: false, dueDate: '2025-02-28' }
    ],
    tags: ['internships', 'career-development', 'STEM']
  },
  {
    id: '5',
    title: 'Build College Application Portfolio',
    description: 'Create a comprehensive portfolio showcasing achievements, projects, and experiences for college applications.',
    category: 'college-prep',
    priority: 'medium',
    status: 'in-progress',
    progress: 40,
    targetDate: '2025-08-01',
    createdDate: '2024-09-15',
    milestones: [
      { id: '5a', title: 'Compile academic achievements', completed: true, dueDate: '2024-10-15' },
      { id: '5b', title: 'Document extracurricular activities', completed: false, dueDate: '2024-12-01' },
      { id: '5c', title: 'Create project showcase', completed: false, dueDate: '2025-03-01' },
      { id: '5d', title: 'Write personal statements', completed: false, dueDate: '2025-06-01' }
    ],
    tags: ['college-applications', 'portfolio', 'documentation']
  },
  {
    id: '6',
    title: 'Learn Spanish to Conversational Level',
    description: 'Achieve conversational fluency in Spanish to enhance college applications and future career prospects.',
    category: 'personal',
    priority: 'low',
    status: 'in-progress',
    progress: 30,
    targetDate: '2025-12-01',
    createdDate: '2024-08-01',
    milestones: [
      { id: '6a', title: 'Complete beginner course', completed: true, dueDate: '2024-10-01' },
      { id: '6b', title: 'Pass intermediate level', completed: false, dueDate: '2025-03-01' },
      { id: '6c', title: 'Practice with native speakers', completed: false, dueDate: '2025-08-01' },
      { id: '6d', title: 'Take proficiency test', completed: false, dueDate: '2025-11-01' }
    ],
    tags: ['language-learning', 'personal-development', 'skills']
  }
];

export const Goals: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedView, setSelectedView] = useState<'grid' | 'list'>('grid');

  const filteredGoals = goals.filter(goal => {
    const categoryMatch = selectedCategory === 'all' || goal.category === selectedCategory;
    const statusMatch = selectedStatus === 'all' || goal.status === selectedStatus;
    return categoryMatch && statusMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in-progress': return 'text-blue-600 bg-blue-100';
      case 'not-started': return 'text-gray-600 bg-gray-100';
      case 'paused': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'academic': return BookOpen;
      case 'extracurricular': return Users;
      case 'personal': return Heart;
      case 'career': return Briefcase;
      case 'college-prep': return Trophy;
      default: return Target;
    }
  };

  const calculateOverallProgress = () => {
    const totalProgress = goals.reduce((sum, goal) => sum + goal.progress, 0);
    return Math.round(totalProgress / goals.length);
  };

  const getGoalStats = () => {
    return {
      total: goals.length,
      completed: goals.filter(g => g.status === 'completed').length,
      inProgress: goals.filter(g => g.status === 'in-progress').length,
      overdue: goals.filter(g => new Date(g.targetDate) < new Date() && g.status !== 'completed').length
    };
  };

  const stats = getGoalStats();

  return (
    <div className="space-y-8">
      {/* Header */}
      <Card className="bg-gradient-to-br from-primary-500 to-accent-500 text-white">
        <div className="text-center">
          <Target className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Goal Management</h2>
          <p className="text-primary-100 text-lg mb-6">
            Set, track, and achieve your academic and personal goals for college success
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-sm text-primary-100">Total Goals</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.inProgress}</div>
              <div className="text-sm text-primary-100">In Progress</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{stats.completed}</div>
              <div className="text-sm text-primary-100">Completed</div>
            </div>
            <div className="bg-white bg-opacity-20 rounded-xl p-4">
              <div className="text-2xl font-bold">{calculateOverallProgress()}%</div>
              <div className="text-sm text-primary-100">Overall Progress</div>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters and Controls */}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter size={16} className="text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filter:</span>
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Categories</option>
              <option value="academic">Academic</option>
              <option value="extracurricular">Extracurricular</option>
              <option value="personal">Personal</option>
              <option value="career">Career</option>
              <option value="college-prep">College Prep</option>
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Status</option>
              <option value="not-started">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <div className="flex border border-gray-300 rounded-lg">
              <button
                onClick={() => setSelectedView('grid')}
                className={`px-3 py-1 text-sm ${selectedView === 'grid' ? 'bg-primary-100 text-primary-700' : 'text-gray-600'}`}
              >
                Grid
              </button>
              <button
                onClick={() => setSelectedView('list')}
                className={`px-3 py-1 text-sm ${selectedView === 'list' ? 'bg-primary-100 text-primary-700' : 'text-gray-600'}`}
              >
                List
              </button>
            </div>
            <Button size="sm">
              <Plus size={16} className="mr-2" />
              New Goal
            </Button>
          </div>
        </div>
      </Card>

      {/* Goals Display */}
      <AnimatePresence mode="wait">
        {selectedView === 'grid' ? (
          <motion.div
            key="grid"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredGoals.map((goal, index) => {
              const CategoryIcon = getCategoryIcon(goal.category);
              const completedMilestones = goal.milestones.filter(m => m.completed).length;

              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card hover className="h-full">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <CategoryIcon className="text-primary-600" size={20} />
                        </div>
                        <div>
                          <Badge className={getStatusColor(goal.status)} size="sm">
                            {goal.status.replace('-', ' ')}
                          </Badge>
                        </div>
                      </div>
                      <div className={`text-sm font-semibold ${getPriorityColor(goal.priority)}`}>
                        {goal.priority} priority
                      </div>
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{goal.title}</h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">{goal.description}</p>

                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">Progress</span>
                        <span className="text-sm font-semibold text-gray-900">{goal.progress}%</span>
                      </div>
                      <ProgressBar progress={goal.progress} color="primary" size="sm" />
                    </div>

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center space-x-1">
                        <Calendar size={14} />
                        <span>{new Date(goal.targetDate).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <CheckCircle size={14} />
                        <span>{completedMilestones}/{goal.milestones.length} milestones</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1 mb-4">
                      {goal.tags.slice(0, 2).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" size="sm">
                          {tag}
                        </Badge>
                      ))}
                      {goal.tags.length > 2 && (
                        <Badge variant="secondary" size="sm">
                          +{goal.tags.length - 2}
                        </Badge>
                      )}
                    </div>

                    <div className="flex space-x-2 mt-auto">
                      <Button variant="outline" size="sm" className="flex-1">
                        <Edit3 size={14} className="mr-2" />
                        Edit
                      </Button>
                      <Button size="sm" className="flex-1">
                        View Details
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="list"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-4"
          >
            {filteredGoals.map((goal, index) => {
              const CategoryIcon = getCategoryIcon(goal.category);
              const completedMilestones = goal.milestones.filter(m => m.completed).length;

              return (
                <motion.div
                  key={goal.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card>
                    <div className="flex items-center space-x-6">
                      <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <CategoryIcon className="text-primary-600" size={24} />
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{goal.title}</h3>
                          <Badge className={getStatusColor(goal.status)} size="sm">
                            {goal.status.replace('-', ' ')}
                          </Badge>
                          <span className={`text-sm font-semibold ${getPriorityColor(goal.priority)}`}>
                            {goal.priority}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-3">{goal.description}</p>
                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} />
                            <span>Due: {new Date(goal.targetDate).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <CheckCircle size={14} />
                            <span>{completedMilestones}/{goal.milestones.length} milestones</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {goal.tags.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="secondary" size="sm">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl font-bold text-primary-600 mb-1">{goal.progress}%</div>
                        <ProgressBar progress={goal.progress} color="primary" size="sm" className="w-20" />
                      </div>

                      <div className="flex space-x-2 flex-shrink-0">
                        <Button variant="outline" size="sm">
                          <Edit3 size={14} />
                        </Button>
                        <Button size="sm">
                          View
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Progress This Month</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Goals Started</span>
              <span className="font-semibold text-green-600">3</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Goals Completed</span>
              <span className="font-semibold text-blue-600">1</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Milestones Achieved</span>
              <span className="font-semibold text-purple-600">8</span>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
          <div className="space-y-3">
            {goals
              .filter(g => new Date(g.targetDate) > new Date())
              .sort((a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime())
              .slice(0, 3)
              .map((goal) => (
                <div key={goal.id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 truncate">{goal.title}</span>
                  <span className="text-xs text-gray-500">
                    {new Date(goal.targetDate).toLocaleDateString()}
                  </span>
                </div>
              ))}
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Breakdown</h3>
          <div className="space-y-3">
            {['academic', 'extracurricular', 'personal', 'career', 'college-prep'].map((category) => {
              const categoryGoals = goals.filter(g => g.category === category);
              const avgProgress = categoryGoals.length > 0
                ? Math.round(categoryGoals.reduce((sum, g) => sum + g.progress, 0) / categoryGoals.length)
                : 0;

              return (
                <div key={category}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 capitalize">
                      {category.replace('-', ' ')}
                    </span>
                    <span className="text-sm font-semibold text-gray-900">{avgProgress}%</span>
                  </div>
                  <ProgressBar progress={avgProgress} color="primary" size="sm" />
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
};